import { useEffect, useMemo, useRef } from 'react'
import { trace, SpanStatusCode, Span } from '@opentelemetry/api'
import { traceName } from '@/lib/traceName'

const tracer = trace.getTracer('jubiel-frontend-ui')

type Attributes = Record<string, string | number | boolean | undefined>

export function useTraceSection(
  feature: string,
  action?: string,
  attributes?: Attributes,
  enabled = true,
) {
  const spanRef = useRef<Span | null>(null)
  const attributeSignature = useMemo(
    () => JSON.stringify(attributes ?? {}),
    [attributes],
  )
  const stableAttributes = useMemo(
    () => (attributes ? { ...attributes } : undefined),
    [attributes],
  )

  useEffect(() => {
    if (!enabled) {
      return
    }

    const span = tracer.startSpan(traceName(feature, action), {
      attributes: stableAttributes,
    })
    spanRef.current = span

    return () => {
      spanRef.current?.setStatus({ code: SpanStatusCode.OK })
      spanRef.current?.end()
      spanRef.current = null
    }
  }, [feature, action, attributeSignature, enabled, stableAttributes])
}

export async function withTrace<T>(
  feature: string,
  action: string,
  callback: (span: Span) => Promise<T> | T,
  attributes?: Attributes,
): Promise<T> {
  let isError = false
  return tracer.startActiveSpan(
    traceName(feature, action),
    { attributes },
    async (span) => {
      try {
        const result = await callback(span)
        return result
      } catch (error) {
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: error instanceof Error ? error.message : String(error),
        })
        isError = true
        throw error
      } finally {
        if (!isError) {
          span.setStatus({ code: SpanStatusCode.OK })
        }
        span.end()
      }
    },
  )
}

